import React, { useState, useEffect } from 'react';
import { ArrowLeft, Home } from 'lucide-react';
import EPCDataCollection from './EPCDataCollection';
import { EPCFormData } from '../../utils/epcGenerator';
import { loadFloorPlanFromFile } from '../../utils/fileUtils';
import type { CurrentPage } from '../../App';
import type { User } from '@supabase/supabase-js';

interface EPCPageProps {
  onNavigate: (page: CurrentPage) => void;
  floorPlanId?: string;
  currentUser: User | null;
}

const EPCPage: React.FC<EPCPageProps> = ({ onNavigate, floorPlanId, currentUser }) => {
  const [siteNotesData, setSiteNotesData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load site notes data for auto-population
  useEffect(() => {
    const loadSiteNotes = async () => {
      if (floorPlanId) {
        try {
          const floorPlanData = await loadFloorPlanFromFile(floorPlanId);
          if (floorPlanData && floorPlanData.siteNotesData) {
            setSiteNotesData(floorPlanData.siteNotesData);
          }
        } catch (error) {
          console.warn('Could not load site notes data:', error);
        }
      }
      setIsLoading(false);
    };

    loadSiteNotes();
  }, [floorPlanId]);

  const handleEPCComplete = (data: EPCFormData, xmlOutput: string) => {
    console.log('EPC Generation Complete:', { data, xmlOutput });
    
    // Here you could save the EPC data or navigate to a results page
    alert(`EPC generated successfully! XML file downloaded. 
    
    Report Reference: ${data.reportReference}
    Assessor: ${data.assessorName}
    Property: ${data.address.line1}
    
    The XML file has been downloaded and is ready for submission to the EPC register.`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      {/* Header */}
      <header className="bg-white shadow-md sticky top-0 z-40 flex-shrink-0 rounded-b-2xl border-b-2 border-blue-400">
        <div className="w-full px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => onNavigate({ page: 'retrofit' })}
              title="Back to Retrofit Hub"
              className="p-2.5 rounded-xl text-gray-600 hover:bg-blue-50 hover:text-blue-600 transition-all duration-200 border border-gray-200 hover:border-blue-300"
            >
              <ArrowLeft size={18} />
            </button>
            <h1 className="text-lg sm:text-xl font-semibold text-gray-900">
              Official EPC Generator
            </h1>
          </div>
          
          <div className="flex items-center space-x-3">
            <div className="hidden sm:block">
              <div className="flex items-center space-x-2 px-3 py-1.5 bg-green-50 border border-green-200 rounded-lg">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-sm font-medium text-green-700">RdSAP 21.0.1 Compliant</span>
              </div>
            </div>
            
            <button
              onClick={() => onNavigate({ page: 'start' })}
              title="Go to Home"
              className="p-2.5 rounded-xl text-gray-600 hover:bg-blue-50 hover:text-blue-600 transition-all duration-200 border border-gray-200 hover:border-blue-300"
            >
              <Home size={18} />
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-4 sm:px-6 py-8">
        {isLoading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <EPCDataCollection
            onComplete={handleEPCComplete}
            siteNotesData={siteNotesData}
            currentUser={currentUser}
          />
        )}
      </main>
    </div>
  );
};

export default EPCPage; 